import React, { useEffect, useState } from 'react';
import { CircularProgress, Paper } from '@material-ui/core';
import { Link, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { getHotspot } from '../../actions/hotspots';
import { deriveHotspotNames, fetchHotspotSpeciesMeta } from '../../lib/hotspotTaxonomy';

function formatDistribution(value) {
  if (!value) return 'Not recorded';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') {
    const entries = Object.entries(value)
      .filter(([, count]) => Number(count) > 0)
      .sort((a, b) => Number(b[1]) - Number(a[1]))
      .slice(0, 6);

    if (!entries.length) return 'Not recorded';
    return entries.map(([bucket, count]) => `${bucket}: ${count}`).join(', ');
  }
  return String(value);
}

export const HotspotDetails = () => {
  const { hotspot, isLoading } = useSelector((state) => state.hotspots);
  const dispatch = useDispatch();
  const { id } = useParams();
  const [speciesMeta, setSpeciesMeta] = useState(null);

  useEffect(() => {
    dispatch(getHotspot(id));
  }, [id, dispatch]);

  useEffect(() => {
    let active = true;
    if (!hotspot?.taxonKey) {
      setSpeciesMeta(null);
      return () => {
        active = false;
      };
    }

    fetchHotspotSpeciesMeta(hotspot.taxonKey).then((meta) => {
      if (!active) return;
      setSpeciesMeta(meta);
    });

    return () => {
      active = false;
    };
  }, [hotspot?.taxonKey]);

  if (isLoading) {
    return (
      <div className="atlas-route-shell atlas-detail-page">
        <Paper elevation={6} className="atlas-detail-card atlas-loading-shell">
          <CircularProgress size="3rem" />
        </Paper>
      </div>
    );
  }

  if (!hotspot) {
    return (
      <div className="atlas-route-shell atlas-detail-page">
        <div className="atlas-empty-state">
          This hotspot record is unavailable right now.
        </div>
      </div>
    );
  }

  const location = hotspot.h3_location || hotspot.h3 || hotspot.item_type_id || id;
  const observations = hotspot.observations_count ?? hotspot.count ?? hotspot.hotspot_count ?? 0;
  const individuals = hotspot.individuals_count ?? hotspot.individual_count ?? 0;
  const resolution = hotspot.resolution ?? 'Unknown';
  const active = hotspot.active ?? hotspot.is_active;
  const { primaryName, secondaryName, scientificName, commonName } = deriveHotspotNames(
    hotspot,
    speciesMeta
  );

  const detailRows = [
    ['Common name', commonName],
    ['Scientific name', scientificName],
    ['H3 cell', location],
    ['Observations', Number(observations).toLocaleString()],
    ['Individuals', Number(individuals).toLocaleString()],
    ['Resolution', String(resolution)],
    ['Active', active === undefined ? 'Unknown' : active ? 'Yes' : 'No'],
    ['Taxon key', hotspot.taxonKey || 'Not supplied'],
  ];

  return (
    <div className="atlas-route-shell atlas-detail-page">
      <section className="atlas-page-header">
        <span className="atlas-eyebrow">Hotspot detail</span>
        <h1>{location}</h1>
        <p>
          This page collects the hotspot metadata currently available from Tellus so you can see
          the focal species, the cell it belongs to, and how the record is distributed through time.
        </p>
        <div className="atlas-pill-row">
          <span className="atlas-pill">{primaryName}</span>
          {secondaryName ? <span className="atlas-pill">{secondaryName}</span> : null}
          <span className="atlas-pill">{Number(observations).toLocaleString()} observations</span>
        </div>
      </section>

      <div className="atlas-detail-grid">
        <section className="atlas-detail-stack">
          <Paper elevation={6} className="atlas-detail-card">
            <span className="atlas-card-kicker">Available metadata</span>
            <div className="atlas-detail-list">
              {detailRows.map(([label, value]) => (
                <span key={label}>
                  <strong>{label}</strong>
                  {value}
                </span>
              ))}
            </div>
            {hotspot?.taxonKey ? (
              <div className="atlas-cta-row">
                <Link className="atlas-primary-link" to={`/species/${hotspot.taxonKey}`}>
                  Open species profile
                </Link>
              </div>
            ) : null}
          </Paper>
        </section>

        <section className="atlas-detail-stack">
          <Paper elevation={6} className="atlas-detail-card">
            <span className="atlas-card-kicker">Seasonality and distribution</span>
            <div className="atlas-detail-list">
              <span>
                <strong>Month distribution</strong>
                {formatDistribution(hotspot.month_dist)}
              </span>
              <span>
                <strong>Week distribution</strong>
                {formatDistribution(hotspot.week_dist)}
              </span>
              <span>
                <strong>Observed-on summary</strong>
                {formatDistribution(hotspot.observed_on)}
              </span>
            </div>
          </Paper>

          <Paper elevation={6} className="atlas-detail-card">
            <span className="atlas-card-kicker">Record notes</span>
            <div className="atlas-detail-list">
              <span>
                <strong>What this is</strong>
                A hotspot is a clustered cell, not a guarantee that the focal species is present at
                every moment within it.
              </span>
              <span>
                <strong>Current display</strong>
                This view now resolves the taxon key into common and scientific names when Tellus
                has a matching species record.
              </span>
            </div>
          </Paper>
        </section>
      </div>
    </div>
  );
};
